// src/components/Hero/HeroSection.js
// Literal markup match of the reference's .a-hero (a-eyebrow/h2/a-meta/
// a-pill/a-play/disc/hero-icon-play/hero-icon-pause/hero-play-label). Wrapped
// by HomeTemplate in <section className="a-section a-section--hero" id="home">
// per the reference's section shell. Real data (fetchHeroEpisode / PlayerContext)
// preserved — only markup/classes changed.
import { useState, useEffect } from "react";
import { usePlayer } from "../../contexts/PlayerContext";
import { fetchHeroEpisode } from "../../api";
import { getHeroThumbnail } from "../../utils/thumbnails";
import "./HeroSection.css";

const HeroSection = ({ onEpisodeLoad }) => {
  const { playEpisode, currentEpisode, isPlaying } = usePlayer();
  const [heroEpisode, setHeroEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadHeroEpisode = async () => {
      try {
        setLoading(true);
        const episode = await fetchHeroEpisode();
        setHeroEpisode(episode);
        setError(null);
        if (onEpisodeLoad) onEpisodeLoad(episode);
      } catch (err) {
        console.error("Failed to fetch hero episode:", err);
        setError(err.message);
        const fallbackEpisode = {
          id: 1,
          title: "Welcome to GUHSO Podcast",
          episode_number: "1",
          season_number: "1",
          show: { title: "GUHSO" },
          thumbnail_url: null,
          audio_url: "/audio/default.mp3",
          itunes_duration: "30:00",
          description:
            "Welcome to our podcast! Join us as we dive into authentic Jamaican conversations, real talk, and cultural discussions that matter. Every episode brings you closer to the culture and community that makes GUHSO special.",
        };
        setHeroEpisode(fallbackEpisode);
        if (onEpisodeLoad) onEpisodeLoad(fallbackEpisode);
      } finally {
        setLoading(false);
      }
    };

    loadHeroEpisode();
  }, [onEpisodeLoad]);

  const handlePlayClick = () => {
    if (heroEpisode) {
      playEpisode({
        id: heroEpisode.id,
        title: heroEpisode.title,
        audioUrl: heroEpisode.audio_url,
        thumbnailUrl: heroEpisode.thumbnail_url || heroEpisode.itunes_image,
      });
    }
  };

  if (loading) {
    return (
      <div className="a-hero is-loading">
        <div className="a-hero-spinner" />
      </div>
    );
  }

  if (error && !heroEpisode) {
    return (
      <div className="a-hero">
        <p className="a-eyebrow">Unable to load</p>
        <h1>Featured episode unavailable</h1>
      </div>
    );
  }

  const episodeForThumbnail = {
    ...heroEpisode,
    thumbnail: heroEpisode.thumbnail_url || heroEpisode.itunes_image,
    heroThumbnail: heroEpisode.hero_thumbnail,
    id: heroEpisode.id,
    slug: heroEpisode.slug,
  };
  const thumbnailUrl = getHeroThumbnail(episodeForThumbnail);

  const hashtags = [
    "#guhso",
    "#podcast",
    heroEpisode.show?.title ? `#${heroEpisode.show.title.toLowerCase()}` : "",
    heroEpisode.episode_number ? `#episode${heroEpisode.episode_number}` : "",
  ].filter(Boolean);

  const episodeNumber = heroEpisode.episode_number || heroEpisode.itunes_episode;
  const duration = heroEpisode.itunes_duration || "30:00";
  const isCurrentAndPlaying = currentEpisode?.id === heroEpisode.id && isPlaying;

  return (
    <div className="a-hero">
      {thumbnailUrl && (
        <img className="a-hero__art" src={thumbnailUrl} alt="" aria-hidden="true" />
      )}
      {episodeNumber && <p className="a-eyebrow">Now Playing &middot; Episode {episodeNumber}</p>}
      <h1>{heroEpisode.title}</h1>
      <div className="a-meta">
        {heroEpisode.season_number && <span className="a-pill">Season {heroEpisode.season_number}</span>}
        {hashtags.map((tag) => (
          <span key={tag} className="a-pill">{tag}</span>
        ))}
      </div>
      <button
        type="button"
        className={`a-play ${isCurrentAndPlaying ? 'is-playing' : ''}`}
        onClick={handlePlayClick}
        aria-label={isCurrentAndPlaying ? 'Pause featured episode' : 'Play featured episode'}
      >
        <span className="disc" aria-hidden="true">
          <svg className="hero-icon-play" viewBox="0 0 24 24"><path d="m9 6 9 6-9 6Z" /></svg>
          <svg className="hero-icon-pause" viewBox="0 0 24 24"><path d="M9 7v10M15 7v10" /></svg>
        </span>
        <span className="hero-play-label">
          {isCurrentAndPlaying ? 'Pause' : 'Play'} Episode &middot; {duration}
        </span>
      </button>
    </div>
  );
};

export default HeroSection;
